import * as ex from 'excalibur';
import { TILE_SIZE, ANIMATION_DURATION } from '../config/constants';
import { Entity } from './entity';
import { ParticleManager } from '../managers/particleManager';
import { AudioManager } from '../managers/audioManager';
import { Animal } from './animal';

/**
 * An interpolation target for an entity
 */
interface MovementTarget {
  entity: Entity;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  startTime: number;
  duration: number;
  // Optional scale variation for bounce effect
  scaleX?: number;
  scaleY?: number;
}

/**
 * Centralized system for handling entity movement interpolation
 * This replaces individual entity movement handling for better performance
 */
export class MovementSystem {
  private static instance: MovementSystem;
  private activeMovements: Map<Entity, MovementTarget> = new Map();
  private defaultDuration: number = ANIMATION_DURATION; // ms
  
  // Private constructor for singleton
  private constructor() {}
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): MovementSystem {
    if (!MovementSystem.instance) {
      MovementSystem.instance = new MovementSystem();
    }
    return MovementSystem.instance;
  }
  
  /**
   * Set the default movement duration
   * @param duration Duration in milliseconds
   */
  public setDefaultDuration(duration: number): void {
    this.defaultDuration = duration;
  }
  
  /**
   * Request an entity to move to a grid position with interpolation
   * @param entity The entity to move
   * @param gridX Target grid X coordinate
   * @param gridY Target grid Y coordinate
   * @param duration Optional custom duration for this movement
   */
  public moveEntityToGrid(entity: Entity, gridX: number, gridY: number, duration?: number): void {
    // Calculate pixel coordinates
    const targetX = gridX * TILE_SIZE + TILE_SIZE / 2;
    const targetY = gridY * TILE_SIZE + TILE_SIZE / 2;
    
    // Update entity's grid position immediately
    // But we'll interpolate the visual position
    entity.gridX = gridX;
    entity.gridY = gridY;
    
    // Store start position
    const startX = entity.pos.x;
    const startY = entity.pos.y;
    
    // Determine horizontal movement direction
    const isMovingRight = targetX > startX;
    const isMovingLeft = targetX < startX;
    
    // Flip sprite based on horizontal movement direction
    // All sprites start facing left by default, so we flip when moving right
    if (isMovingRight) {
      // Set negative scale on X-axis to flip the sprite horizontally
      entity.scale.x = -Math.abs(entity.scale.x);
    } else if (isMovingLeft) {
      // Reset to positive scale on X-axis (facing left, which is default)
      entity.scale.x = Math.abs(entity.scale.x);
    }
    
    // Store original scale if entity has one
    const originalScaleX = entity.scale.x;
    const originalScaleY = entity.scale.y;
    
    // Create dust effect and play hop sound
    // Only for animals and only if they're actually moving
    if (entity instanceof Animal && (startX !== targetX || startY !== targetY)) {
      ParticleManager.getInstance().createDustEffect(entity, startX, startY);
      
      // Reduce chance of sound triggering when many animals move at once
      // 80% chance for player-controlled entities, 20% chance for others
      const isPlayerControlled = entity.hasOwnProperty('isPlayerControlled');
      const soundProbability = isPlayerControlled ? 0.8 : 0.2;
      
      if (Math.random() < soundProbability) {
        // Play hop sound for animal movement with spatial audio
        // Pass the entity's current position for proper panning
        void AudioManager.getInstance().playHopSound(startX, startY);
      }
    }
    
    // Add to active movements
    this.activeMovements.set(entity, {
      entity,
      startX,
      startY,
      targetX,
      targetY,
      startTime: Date.now(),
      duration: duration ?? this.defaultDuration,
      scaleX: originalScaleX,
      scaleY: originalScaleY
    });
  }
  
  /**
   * Update all active movements
   * Should be called once per frame
   */
  public update(): void {
    const now = Date.now();
    
    // Process each active movement
    for (const [entity, movement] of this.activeMovements.entries()) {
      const elapsed = now - movement.startTime;
      const progress = Math.min(elapsed / movement.duration, 1);
      
      if (progress < 1) {
        // Calculate horizontal movement with linear interpolation
        const newX = movement.startX + (movement.targetX - movement.startX) * progress;
        
        // Calculate vertical movement with a hop and bounce effect
        let newY;
        // Pre-calculate normalized progress values we'll need
        const hopProgress = progress < 0.5 ? progress * 2 : 0; // 0-1 for first half
        const fallProgress = progress >= 0.5 ? (progress - 0.5) * 2 : 0; // 0-1 for second half
        
        // Bounce calculation for the second half (we'll use this for squash as well)
        const bounceFactor = 5; // Controls bounce frequency
        const bounceAmount = 0.08; // Controls bounce height (proportion of TILE_SIZE)
        const bounce = progress >= 0.5 ? 
                       Math.pow(2, -10 * fallProgress) * 
                       Math.sin((fallProgress - 0.1) * bounceFactor * Math.PI) + 1 : 0;
        
        if (progress < 0.5) {
          // First half: rise up in an arc (hop)
          // Use sine curve for smooth arc up
          const hopFactor = 0.4; // Maximum height of hop (as proportion of tile size)
          const hopHeight = TILE_SIZE * hopFactor; 
          
          // Calculate path along a parabola for more natural hop
          newY = movement.startY + (movement.targetY - movement.startY) * hopProgress - 
                 hopHeight * Math.sin(hopProgress * Math.PI);
        } else {
          // Second half: fall down with elastic bounce
          // Combine linear interpolation with bounce effect
          newY = movement.startY + (movement.targetY - movement.startY) * 
                (0.5 + fallProgress * 0.5) - (TILE_SIZE * bounceAmount * bounce);
        }
        
        // Add a squash and stretch effect based on vertical movement
        // Stretch when going up, squash when landing
        let scaleModX = 1.0;
        let scaleModY = 1.0;
        
        if (progress < 0.5) {
          // Stretching during rise - taller and thinner
          const stretchFactor = 0.15; // How much to stretch/squash
          const stretchAmount = Math.sin(hopProgress * Math.PI) * stretchFactor;
          scaleModX = 1.0 - stretchAmount;
          scaleModY = 1.0 + stretchAmount;
        } else {
          // Squashing during landing - wider and shorter
          const squashFactor = 0.2; // How much to squash at peak
          // More squash at beginning of landing, gradually returning to normal
          const squashAmount = (1 - fallProgress) * squashFactor * bounce;
          scaleModX = 1.0 + squashAmount;
          scaleModY = 1.0 - squashAmount * 0.8; // Less vertical squash
        }
        
        // Apply scale modification, preserving the direction (sign of X scale)
        const directionMultiplier = movement.scaleX < 0 ? -1 : 1;
        entity.scale = new ex.Vector(
          Math.abs(movement.scaleX) * scaleModX * directionMultiplier, 
          movement.scaleY * scaleModY
        );
        
        // Update entity position
        entity.pos = new ex.Vector(newX, newY);
      } else {
        // Movement complete
        entity.pos = new ex.Vector(movement.targetX, movement.targetY);
        // Reset scale to original, preserving the direction (sign of X scale)
        const directionMultiplier = movement.scaleX < 0 ? -1 : 1;
        entity.scale = new ex.Vector(
          Math.abs(movement.scaleX) * directionMultiplier, 
          movement.scaleY
        );
        this.activeMovements.delete(entity);
      }
    }
  }
  
  /**
   * Check if an entity is currently moving
   * @param entity The entity to check
   * @returns True if the entity has an active movement
   */
  public isMoving(entity: Entity): boolean {
    return this.activeMovements.has(entity);
  }
  
  /**
   * Get the number of active movements
   * Useful for debugging
   */
  public getActiveMovementCount(): number {
    return this.activeMovements.size;
  }
  
  /**
   * Cancel all active movements
   * Useful when pausing the game
   */
  public cancelAllMovements(): void {
    // Teleport all entities to their target positions
    for (const [entity, movement] of this.activeMovements.entries()) {
      entity.pos = new ex.Vector(movement.targetX, movement.targetY);
    }
    
    // Clear the movement map
    this.activeMovements.clear();
  }
}